import {
  prisma,
  GatewayType,
  PaymentType,
  PaymentGateway,
  EFawateerCom,
} from "../../client";

/**
 *
 * @param eFawateerCom
 * @param paymentGateway
 * @returns boolean if the gateway is a delayed payment confirmation gateway or offline payment gateway
 */
const isDelayedConfirmationGateway = (
  eFawateerCom: Pick<EFawateerCom, "receiverCode" | "serviceType"> | null,
  paymentGateway: Pick<PaymentGateway, "paymentType" | "type">
): boolean => {
  const isEFawateerComPayment =
    !!eFawateerCom && paymentGateway.type === GatewayType.EFAWATEERCOM;

  const isOfflinePaymentGateway =
    paymentGateway.paymentType === PaymentType.OFFLINE;

  return isOfflinePaymentGateway || isEFawateerComPayment;
};

/**
 * Fetch gateway configuration for a given gatewayMethodId, selecting prod/sandbox by NODE_ENV.
 * @param gatewayMethodId
 * @returns gateway configuration
 */
export const getGatewayConfigurationByGatewayMethodId = async (
  gatewayMethodId: string
) => {
  if (!gatewayMethodId) return null;

  const gatewayMethod = await prisma.gatewayMethod.findUnique({
    where: { id: gatewayMethodId },
    select: {
      gatewayConfiguration: {
        select: {
          hyperpay: {
            select: {
              entityId: true,
              accessToken: true,
              entityIdSandbox: true,
              accessTokenSandbox: true,
            },
          },
          eFawateerCom: {
            select: {
              receiverCode: true,
              serviceType: true,
            },
          },
          wireTransfer: {
            select: {
              accountHolderName: true,
              bankName: true,
              ibanNumber: true,
              bankAccountNumber: true,
              bankBranch: true,
              bankAddress: true,
              bankCity: true,
              bankCountry: true,
              swiftCode: true,
            },
          },
          cliq: {
            select: {
              alias: true,
              accountHolderName: true,
              accountHolderNumber: true,
              bankName: true,
              instructions: true,
            },
          },
          pos: {
            select: {
              instructions: true,
            },
          },
          paymentGateway: {
            select: {
              paymentType: true,
              type: true,
              currency: true,
            },
          },
        },
      },
    },
  });

  const config = gatewayMethod?.gatewayConfiguration;
  if (!config) return null;

  const { paymentGateway, hyperpay, eFawateerCom, wireTransfer, cliq, pos } =
    config;
  const gatewayName = paymentGateway.type;
  const currency = paymentGateway.currency;
  if (!gatewayName || !currency) return null;

  const isDelayedPaymentGateway = isDelayedConfirmationGateway(
    eFawateerCom,
    paymentGateway
  );

  switch (gatewayName) {
    case GatewayType.HYPERPAY: {
      if (!hyperpay) return null;

      const isProd = process.env.NODE_ENV === "production";
      const entityId = isProd ? hyperpay.entityId : hyperpay.entityIdSandbox;
      const accessToken = isProd
        ? hyperpay.accessToken
        : hyperpay.accessTokenSandbox;

      if (!entityId || !accessToken) return null;

      return {
        gatewayName,
        currency,
        credentials: { entityId, accessToken },
        isDelayedPaymentGateway,
      };
    }
    case GatewayType.EFAWATEERCOM: {
      if (!eFawateerCom) return null;

      return {
        gatewayName,
        currency,
        credentials: { ...eFawateerCom },
        isDelayedPaymentGateway,
      };
    }
    case GatewayType.WIRE_TRANSFER: {
      if (!wireTransfer) return null;

      return {
        gatewayName,
        currency,
        credentials: { ...wireTransfer },
        isDelayedPaymentGateway,
      };
    }
    case GatewayType.CLIQ: {
      if (!cliq) return null;

      return {
        gatewayName,
        currency,
        credentials: { ...cliq },
        isDelayedPaymentGateway,
      };
    }
    case GatewayType.POS: {
      return {
        gatewayName,
        currency,
        credentials: pos ? { ...pos } : null,
        isDelayedPaymentGateway,
      };
    }
    case GatewayType.PAYFORT:
    case GatewayType.STRIPE:
    case GatewayType.PAYPAL:
    default:
      return {
        gatewayName,
        currency,
        credentials: null,
        isDelayedPaymentGateway,
      };
  }
};
